import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

/**
 * EngineeredStackDiagram — a true architectural cross-section view of
 * the Guardian build-up. Replaces the previous bar-style list with a
 * blueprint-grade visual: proportional bands, hatched / dotted material
 * fills, depth annotations, and a side specs rail. Designed for the
 * dark "Our Difference" section.
 *
 * Bands run TOP -> BOTTOM in the visual order they appear on site:
 *   Premium Lawn (surface)
 *   Geotextile Membrane (hairline)
 *   10–1mm Granite Sub-Base
 *   Type 3 Aggregate (deepest structural layer)
 *   Excavation line (datum)
 */

const BANDS = [
  {
    key: 'lawn',
    label: 'Premium Lawn',
    sub: 'Hand-cut, taped, glued, pinned, infilled',
    depthLabel: '+0–45mm',
    depthMm: 45,
    fill: 'url(#grass-pattern)',
    accent: '#6A8F3E',
    stepLabel: 'Surface',
  },
  {
    key: 'membrane',
    label: 'Geotextile Membrane',
    sub: 'Weed suppression + install stability',
    depthLabel: 'Hairline',
    depthMm: 4, // visually exaggerated so it reads
    fill: '#243B26',
    accent: '#9DB87A',
    hairline: true,
    stepLabel: 'Membrane',
  },
  {
    key: 'granite',
    label: 'Standard Free-Draining Subbase',
    sub: 'Free-draining 10mm–1mm compacted crushed granite, precision screeded for a glass-smooth surface.',
    depthLabel: '50mm',
    depthMm: 50,
    fill: 'url(#granite-pattern)',
    accent: '#C7CFC0',
    stepLabel: 'Standard Subbase',
  },
  {
    key: 'aggregate',
    label: 'Additional Drainage Layer',
    sub: 'Free-draining Type 3 aggregate, compacted to 100mm — added on projects that need extra drainage.',
    depthLabel: '100mm',
    depthMm: 100,
    fill: 'url(#aggregate-pattern)',
    accent: '#A8AEA0',
    optional: true,
    stepLabel: 'If required',
  },
  {
    key: 'formation',
    label: 'Formation Reduce Dig',
    sub: 'Oversite reduce dig to suit project requirements. 50mm typical, up to 150mm for enhanced drainage.',
    depthLabel: '50–150mm',
    depthMm: 32, // visual cap so the diagram doesn't dwarf the others
    fill: 'url(#earth-pattern)',
    accent: '#6B5A47',
    earth: true,
    stepLabel: 'Formation',
  },
];

export default function EngineeredStackDiagram() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-10% 0px' });

  const totalMm = BANDS.reduce((s, b) => s + b.depthMm, 0);

  // Pre-compute cumulative tops as percentages so SVG bands stack correctly.
  let cursor = 0;
  const bandsWithGeom = BANDS.map((b) => {
    const top = (cursor / totalMm) * 100;
    const h = (b.depthMm / totalMm) * 100;
    cursor += b.depthMm;
    return { ...b, top, h };
  });

  // Total depth removed at client request — depth now varies project-to-
  // project depending on drainage needs (50mm typical → 150mm enhanced).
  // Stating a single number would be misleading.

  return (
    <div ref={ref} className="relative min-w-0">
      {/* Header strip */}
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2 mb-6 pb-4 border-b border-[#E7E9E4]/12">
        <div className="min-w-0">
          <div className="text-[9px] tracking-[0.35em] sm:tracking-[0.45em] uppercase text-[#6A8F3E]/80 font-bold">
            Cross-Section
          </div>
          <h3 className="font-serif-editorial text-xl sm:text-2xl md:text-[1.85rem] tracking-tight text-[#E7E9E4] mt-2 leading-tight">
            The Engineered Stack
          </h3>
        </div>
        <div className="text-left sm:text-right shrink-0">
          <div className="text-[9px] tracking-[0.32em] uppercase text-[#E7E9E4]/45 font-semibold">Project</div>
          <div className="font-serif-editorial text-sm md:text-base text-[#E7E9E4] leading-tight mt-1">
            Depth on site
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,9.5rem)_minmax(0,1fr)] xl:grid-cols-[minmax(0,11rem)_minmax(0,1fr)] gap-x-6 gap-y-5 min-w-0">
        {/* Visual cross section — full width on small screens, spans the spec rows on large */}
        <div className="relative min-w-0 lg:row-span-5 lg:h-full">
          <svg
            viewBox="0 0 200 420"
            preserveAspectRatio="none"
            className="w-full h-[220px] sm:h-[260px] lg:h-full lg:min-h-[360px] block"
            aria-label="Cross-section diagram of Guardian engineered lawn build-up"
          >
            <defs>
              {/* Grass blades pattern */}
              <pattern id="grass-pattern" patternUnits="userSpaceOnUse" width="14" height="22">
                <rect width="14" height="22" fill="#3E5F2B" />
                <path d="M2 22 L2 8 M5 22 L5 4 M8 22 L8 9 M11 22 L11 6" stroke="#6A8F3E" strokeWidth="1.2" strokeLinecap="round" opacity="0.85" />
                <path d="M3.5 22 L3.5 11 M6.5 22 L6.5 7 M9.5 22 L9.5 12" stroke="#8FAE57" strokeWidth="0.8" strokeLinecap="round" opacity="0.7" />
              </pattern>

              {/* Granite — fine dotted texture */}
              <pattern id="granite-pattern" patternUnits="userSpaceOnUse" width="6" height="6">
                <rect width="6" height="6" fill="#2A3A28" />
                <circle cx="1.5" cy="1.5" r="0.7" fill="#C7CFC0" />
                <circle cx="4.5" cy="3.5" r="0.5" fill="#9DB87A" />
                <circle cx="2.5" cy="5" r="0.6" fill="#C7CFC0" opacity="0.7" />
              </pattern>

              {/* Type 3 aggregate — chunky stones */}
              <pattern id="aggregate-pattern" patternUnits="userSpaceOnUse" width="18" height="18">
                <rect width="18" height="18" fill="#1F2A1F" />
                <polygon points="3,3 7,2 8,6 5,8 2,7" fill="#5C6B58" />
                <polygon points="11,4 15,5 14,9 10,8" fill="#7A8773" opacity="0.85" />
                <polygon points="4,11 9,12 8,16 3,15" fill="#6A7864" />
                <polygon points="12,12 17,13 16,17 13,17" fill="#4A5648" />
              </pattern>

              {/* Earth / subsoil — diagonal hatching */}
              <pattern id="earth-pattern" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                <rect width="8" height="8" fill="#3A2C20" />
                <line x1="0" y1="0" x2="0" y2="8" stroke="#6B5A47" strokeWidth="1.3" />
              </pattern>

              {/* Soft inner shadow for depth */}
              <linearGradient id="band-shade" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#000" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#000" stopOpacity="0" />
              </linearGradient>
            </defs>

            {/* Frame border */}
            <rect x="1" y="1" width="198" height="418" fill="none" stroke="rgba(106,143,62,0.35)" strokeWidth="1" rx="3" />

            {bandsWithGeom.map((b, i) => {
              const yPct = b.top * 4.2; // 0..420
              const hPct = b.h * 4.2;
              return (
                <motion.g
                  key={b.key}
                  initial={{ opacity: 0, y: -10 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.7, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                >
                  <rect x="2" y={yPct} width="196" height={hPct} fill={b.fill} />
                  <rect x="2" y={yPct} width="196" height={hPct} fill="url(#band-shade)" />
                  {/* dividing rule */}
                  {i > 0 && (
                    <line x1="2" y1={yPct} x2="198" y2={yPct} stroke="rgba(231,233,228,0.18)" strokeWidth="0.6" strokeDasharray="2 3" />
                  )}
                </motion.g>
              );
            })}

            {/* Side depth tick marks */}
            {bandsWithGeom.map((b, i) => {
              const yMid = (b.top + b.h / 2) * 4.2;
              return (
                <motion.g
                  key={b.key + '-tick'}
                  initial={{ opacity: 0 }}
                  animate={inView ? { opacity: 1 } : {}}
                  transition={{ delay: 0.5 + i * 0.12, duration: 0.5 }}
                >
                  <line x1="196" y1={yMid} x2="202" y2={yMid} stroke="#6A8F3E" strokeWidth="1.2" />
                </motion.g>
              );
            })}
          </svg>

          {/* Surface label */}
          <div className="absolute -top-3 left-0 right-0 text-center pointer-events-none">
            <span className="inline-block text-[8px] tracking-[0.4em] uppercase text-[#6A8F3E]/80 font-bold bg-[#162A1E] px-2">
              Surface
            </span>
          </div>
          <div className="absolute -bottom-3 left-0 right-0 text-center pointer-events-none">
            <span className="inline-block text-[8px] tracking-[0.4em] uppercase text-[#6A8F3E]/50 font-bold bg-[#162A1E] px-2">
              Subsoil
            </span>
          </div>
        </div>

        {bandsWithGeom.map((b, i) => (
          <motion.div
            key={b.key + '-row'}
            initial={{ opacity: 0, x: 16 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.25 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
            className="relative min-w-0 flex items-start lg:items-center gap-3"
          >
            <div className="min-w-0 flex-1">
              <div className="text-[9px] sm:text-[10px] tracking-[0.18em] sm:tracking-[0.22em] uppercase text-[#E7E9E4]/45 font-semibold mb-1 break-words">
                {`0${i + 1}`} · {b.stepLabel || 'Layer'}
              </div>
              <div className="text-[13px] sm:text-sm font-semibold text-[#E7E9E4] tracking-tight leading-snug break-words">
                {b.label}
              </div>
              <div className="text-[11.5px] text-[#E7E9E4]/55 mt-1 leading-snug break-words">
                {b.sub}
              </div>
            </div>
            <div className="shrink-0 text-right pt-0.5">
              <div className="font-serif-editorial text-base sm:text-lg xl:text-xl text-[#E7E9E4] tabular-nums leading-none">
                {b.depthLabel}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Footer caption */}
      <div className="mt-6 pt-4 border-t border-[#E7E9E4]/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-[9px] sm:text-[10px] tracking-[0.18em] sm:tracking-[0.22em] uppercase text-[#E7E9E4]/45 font-semibold">
        <span>Drawing not to scale</span>
        <span>Guardian · Specification Sheet</span>
      </div>
    </div>
  );
}
